import React, { useState } from "react";

import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Icon } from "@iconify/react";
import { Button, Modal, Row, Space, Typography } from "antd";
import { toast } from "utils/toast";

const { Title, Text, Paragraph } = Typography;

interface ShareModalProps {
  open: boolean;
  onCancel: () => void;
  contractLink: string;
  tenantPhoneNumber?: string;
  landlordPhoneNumber?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onCancel,
  contractLink,
  tenantPhoneNumber,
  landlordPhoneNumber,
}) => {
  // Function to truncate URL for better display
  const truncateUrl = (url: string, maxLength: number = 60): string => {
    if (url.length <= maxLength) return url;

    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      const path = urlObj.pathname + urlObj.search;

      // Show domain + "..." + last part of path
      const domainPart = `${urlObj.protocol}//${domain}`;
      const remainingLength = maxLength - domainPart.length - 3; // 3 for "..."

      if (remainingLength > 10) {
        const pathEnd = path.slice(-remainingLength);
        return `${domainPart}...${pathEnd}`;
      } else {
        // If domain is too long, just truncate the whole URL
        return url.slice(0, maxLength - 3) + "...";
      }
    } catch {
      // If URL parsing fails, just truncate normally
      return url.slice(0, maxLength - 3) + "...";
    }
  };

  const [showFullUrl, setShowFullUrl] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(contractLink)
      .then(() => {
        toast.success("Enlace copiado al portapapeles");
      })
      .catch(() => {
        toast.error(
          "No se pudo copiar el enlace. Por favor, cópialo manualmente.",
        );
      });
  };

  const phoneNumbers = [
    { label: "Inquilino", number: tenantPhoneNumber },
    { label: "Propietario", number: landlordPhoneNumber },
  ];

  const renderWhatsAppButton = (label: string, number?: string) =>
    number && (
      <Button
        variant="solid"
        color={"green"}
        onClick={() => {
          const whatsappMessage = `Hola,\n\nTe comparto el contrato de arrendamiento. Puedes consultarlo en el siguiente enlace: ${contractLink}`;

          window.open(
            `https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage)}`,
            "_blank",
          );
        }}
      >
        <Icon icon="ic:baseline-whatsapp" fontSize={20} /> {label}
      </Button>
    );

  return (
    <Modal
      title={<Title level={4}>Compartir Contrato de Arrendamiento</Title>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        {/* Instructions */}
        <div>
          <Title level={5}>Instrucciones para compartir:</Title>
          <Paragraph>
            1. Copia el enlace del contrato
            <br />
            2. Compártelo con tus clientes (inquilino y/o propietario) a través
            del medio de tu preferencia
            <br />
            3. El cliente podrá acceder al contrato usando el enlace compartido
          </Paragraph>
          <Paragraph>
            <strong>NOTA</strong>: Este enlace permite acceder al contrato de
            forma permanente. Compártelo solo con las personas autorizadas.
          </Paragraph>
        </div>

        {/* WhatsApp Share Options */}
        <div style={{ marginTop: "16px" }}>
          <Text strong>Compartir por WhatsApp:</Text>
          <Row justify={"center"} style={{ marginTop: "8px" }}>
            <Space wrap>
              {phoneNumbers.map(({ label, number }) =>
                renderWhatsAppButton(label, number),
              )}
            </Space>
          </Row>
        </div>

        {/* Link Section */}
        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong>Enlace del contrato:</Text>
            <Button
              type="text"
              size="small"
              icon={showFullUrl ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setShowFullUrl(!showFullUrl)}
              title={
                showFullUrl ? "Ocultar URL completa" : "Mostrar URL completa"
              }
              style={{ fontSize: "12px" }}
            >
              {showFullUrl ? "Ocultar" : "Ver completo"}
            </Button>
          </div>
          <div
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "6px",
              marginTop: "8px",
              wordBreak: showFullUrl ? "break-all" : "break-word",
              fontSize: showFullUrl ? "11px" : "13px",
              lineHeight: showFullUrl ? "1.3" : "1.4",
              maxHeight: showFullUrl ? "none" : "60px",
              overflow: showFullUrl ? "visible" : "hidden",
            }}
          >
            <Text code>
              {showFullUrl ? contractLink : truncateUrl(contractLink)}
            </Text>
          </div>
          <Button
            icon={<CopyOutlined />}
            onClick={handleCopyLink}
            color="primary"
            variant="outlined"
            block
            style={{ marginTop: "8px" }}
          >
            Copiar Enlace
          </Button>
        </div>
      </Space>
    </Modal>
  );
};

export default ShareModal;
